import { css } from '@emotion/css';
import { useState } from 'react';
import { Layout } from '../../shared/components/layout/Layout';
import { HistoryLoadingState } from '../../shared/components/loading-states/HistoryLoadingState';
import { NoActivityYet } from '../../shared/components/NoActivityYet';
import { TabSwitch } from '../../shared/components/TabSwitch';
import { useUser } from '../../shared/providers/user-provider';
import { useReceipts } from '../../shared/services/api/receipt';
import { usePaymentOrders } from '../../shared/services/api/order';
import { Colors } from '../../shared/styles/colors';
import {
  h1Style,
  tabularNumbersStyle,
  valueStyle,
} from '../../shared/styles/text';
import { ReceiptsList } from './ReceiptsList';
import { OrdersList } from './OrdersList';
import { NoPermission } from '../../shared/components/NoPermission';
import { BOTTOM_NAV_SAFE_HEIGHT } from '../../shared/styles/css-values';
import { Motion, Radius, unstyledButton } from '../../shared/styles/tokens';

const TABS = ['sessions', 'payments'];

export const HistoryPage = (): JSX.Element => {
  const user = useUser();
  const [activeTab, setActiveTab] = useState(0);

  const {
    data: receipts,
    isLoading: isLoadingReceipts,
    isRefetching: isRefetchingReceipts,
    isError: isErrorReceipts,
  } = useReceipts();

  const {
    data: paymentOrders,
    isLoading: isLoadingPaymentOrders,
    isRefetching: isRefetchingPaymentOrders,
    isError: isErrorPaymentOrders,
  } = usePaymentOrders();

  if (isErrorReceipts || isErrorPaymentOrders) {
    return <p className={errorStyle}>History could not be loaded.</p>;
  }
  if (!user) {
    return <p className={errorStyle}>User not found</p>;
  }

  const isLoading =
    isLoadingReceipts ||
    isRefetchingReceipts ||
    isLoadingPaymentOrders ||
    isRefetchingPaymentOrders;

  return (
    <Layout.Content className={historyPageStyle}>
      <div className={headerContainerStyle}>
        <h1>History</h1>
      </div>
      {user.charter ? (
        <NoPermission />
      ) : (
        <>
          <TabSwitch
            tabs={TABS}
            selectedIndex={activeTab}
            setIndex={setActiveTab}
            className={tabSwitchStyle}
          />
          {isLoading ? (
            <HistoryLoadingState />
          ) : activeTab === 0 ? (
            receipts && receipts.length > 0 ? (
              <ReceiptsList receipts={receipts} />
            ) : (
              <NoActivityYet />
            )
          ) : paymentOrders && paymentOrders.length > 0 ? (
            <OrdersList paymentOrders={paymentOrders} />
          ) : (
            <NoActivityYet />
          )}
        </>
      )}
    </Layout.Content>
  );
};

const historyPageStyle = css`
  padding-top: 0.75rem;
  padding-bottom: calc(${BOTTOM_NAV_SAFE_HEIGHT} + 3rem);
`;

const errorStyle = css`
  padding: 2rem 1.5rem;
  color: ${Colors.TEXT_SECONDARY};
`;

const tabSwitchStyle = css`
  margin-bottom: 0.5rem;
`;

const headerContainerStyle = css`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  > h1 {
    ${h1Style}
  }
`;

export const historyListStyle = css`
  ${valueStyle}
  list-style: none;
  margin-top: 1rem;

  > li + li {
    border-top: 1px solid ${Colors.BORDER};
  }
`;

export const listItem = css`
  ${unstyledButton}
  width: 100%;
  padding: 0.875rem 0.5rem;
  margin: 0 -0.5rem;
  width: calc(100% + 1rem);
  border-radius: ${Radius.md};
  cursor: pointer;
  transition: background-color ${Motion.fast};

  @media (hover: hover) {
    &:hover {
      background: ${Colors.SURFACE};
    }
  }

  &:active {
    background: ${Colors.SURFACE_MUTED};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: -2px;
  }
`;

export const listItemContainerStyle = css`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  ${tabularNumbersStyle}
`;

export const listItemTopContainerStyle = css`
  margin-bottom: 0.375rem;
  font-weight: bold;
`;

export const locationNameStyle = css`
  color: ${Colors.TEXT_SECONDARY};
  overflow-wrap: anywhere;
`;

const statusPillStyle = css`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  font-size: 0.8125rem;
  font-weight: bold;
  line-height: 1.25rem;
  padding: 0.125rem 0.625rem;
  border-radius: ${Radius.pill};
  white-space: nowrap;
`;

export const paymentStatusStyle = css`
  ${statusPillStyle}
  color: ${Colors.SUCCESS};
  background: ${Colors.SUCCESS_TINT};
`;
export const pendingPaymentStyle = css`
  ${statusPillStyle}
  color: ${Colors.CAPRI_BLUE};
  background: ${Colors.CAPRI_BLUE_TINT};
`;
